import React, { useEffect } from 'react';
import {
    PushNotifications,
    Token,
    PushNotificationSchema,
    ActionPerformed,
} from '@capacitor/push-notifications';
import { PluginListenerHandle } from '@capacitor/core';
import { User, useCreateOrUpdatePushTokenMutation } from '../gql/types';
import { Device } from '@capacitor/device';
import { isNative } from '../platform/platform';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';

interface PushNotificationHandlerProps {
    user: User | null;
}

const PushNotificationHandler: React.FC<PushNotificationHandlerProps> = ({ user }) => {
    const [createOrUpdateToken] = useCreateOrUpdatePushTokenMutation();
    const navigate = useNavigate();
    useEffect(() => {
        // no registration until user signs in
        if (user === null || !isNative()) {
            return;
        }
        let regListener: PluginListenerHandle | undefined;
        let regErrorListener: PluginListenerHandle | undefined;
        let notificationListener: PluginListenerHandle | undefined;
        let actionListener: PluginListenerHandle | undefined;
        let resumeListener: { remove: () => void } | undefined;

        const registerPush = async () => {
            const permission = await PushNotifications.checkPermissions();
            if (permission.receive === 'granted') {
                await PushNotifications.register();
            } else {
                console.log('Push permission not granted (on resume or init).');
            }
        };

        async function setupPushNotifications() {
            const permission = await PushNotifications.requestPermissions();
            if (permission.receive === 'granted') {
                await PushNotifications.register();
            } else {
                console.error('Push notifications permission not granted');
            }

            regListener = await PushNotifications.addListener('registration', async (token: Token) => {
                try {
                    const { identifier: deviceID } = await Device.getId();

                    console.log('Push registration success, token:', token.value, 'deviceID:', deviceID);

                    await createOrUpdateToken({
                        variables: {
                            deviceID: deviceID,
                            token: token.value,
                        },
                    });
                } catch (err) {
                    console.error('Error registering push token with backend:', err);
                }

            });

            regErrorListener = await PushNotifications.addListener('registrationError', (error: any) => {
                console.error('Error on registration:', JSON.stringify(error));
            });

            notificationListener = await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
                console.log('Push received:', notification);
            });

            actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
                console.log('Push action performed:', notification);
                const data = notification.notification.data;

                if (!data?.entityID || !data?.type) {
                    console.warn("Incomplete push notification payload:", data);
                    return;
                }

                if (data?.entityID && data?.type?.toUpperCase() === "PLAYDATE_INVITE") {
                    navigate(`/notification/playdate/${data.entityID}`);
                } else {
                    console.warn("Unhandled notification type:", data.type);
                }
            });

            resumeListener = await App.addListener('resume', registerPush);
        }

        setupPushNotifications();

        return () => {
            regListener?.remove();
            regErrorListener?.remove();
            notificationListener?.remove();
            actionListener?.remove();
            resumeListener?.remove();
        };
    }, [user]);

    return null;
};

export default PushNotificationHandler;
